import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../Web/components/header';
import Footer from '../Web/components/footer';

const pages = [
  {
    id: 'home',
    title: 'Home',
    path: '/',
    description: 'Welcome to iFLO Pro - Smart HVAC Solutions'
  },
  {
    id: 'contact',
    title: 'Support',
    path: '/contact-us', 
    description: 'Get help and support for iFLO Pro'
  },
  {
    id: 'about',
    title: 'About',
    path: '/iflo-insurance',
    description: 'Learn about iFLO Pro and our mission'
  },
  {
    id: 'formula',
    title: 'iFLO Pro Formula',
    path: '/about',
    description: 'Learn about the iFLO Pro Formula and how it works'
  },
  {
    id: 'news',
    title: 'News',
    path: '/news',
    description: 'Latest news and updates from iFLO Pro'
  },
  {
    id: 'signup',
    title: 'Become A Pro',
    path: '/signup',
    description: 'Join iFLO Pro as a service provider'
  },
  {
    id: 'insurance',
    title: 'iFLO Insurance',
    path: '/iflo-insurance',
    description: 'Insurance solutions for HVAC professionals'
  },
  {
    id: 'blog',
    title: 'Blog',
    path: '/blog',
    description: 'Articles and insights from iFLO Pro'
  }
];

const SearchPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    try {
      const results = pages.filter(page => {
        const searchTerm = searchQuery.toLowerCase();
        return (
          page.title.toLowerCase().includes(searchTerm) ||
          page.description.toLowerCase().includes(searchTerm)
        );
      });
      
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResultClick = (path) => {
    navigate(path);
  };

  return (
    <>
      <Helmet>
        <title>iFLO Pro | Search</title>
        <meta name="description" content="Search for pages on iFLO Pro" />
      </Helmet>

      <Header />
      <div className="min-h-screen bg-white mt-24 py-8 px-4 sm:px-6 lg:px-8 transition-all duration-300">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 animate-fade-in">
            <h1 className="text-4xl font-bold text-black sm:text-5xl transform hover:scale-105 transition-transform duration-300">
              Search
            </h1>
            <p className="mt-2 text-lg text-gray-600 animate-slide-up">
              Find what you're looking for
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8 transform hover:-translate-y-1 transition-all duration-300">
            <div className="relative group">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  handleSearch({ preventDefault: () => {}, target: { value: e.target.value } });
                }}
                placeholder="Search pages..."
                className="w-full px-4 py-3 pl-12 text-lg bg-white text-black border-2 border-gray-200 rounded-lg 
                          focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-300
                          group-hover:shadow-lg"
              />
              <button
                type="submit"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-yellow-500 text-black px-6 py-2 
                         rounded-md hover:bg-yellow-400 transition-colors duration-300 font-semibold"
              >
                Search
              </button>
            </div>
          </form>

          {/* Search Results */}
          <div className="max-w-4xl mx-auto">
            {isLoading ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
                <p className="mt-4 text-black">Searching...</p>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="space-y-6">
                {searchResults.map((result, index) => (
                  <div
                    key={result.id}
                    className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 
                             cursor-pointer border border-gray-100 transform hover:-translate-y-1
                             animate-fade-in-up"
                    style={{ animationDelay: `${index * 100}ms` }}
                    onClick={() => handleResultClick(result.path)}
                  >
                    <h3 className="text-xl font-semibold text-black mb-2 group-hover:text-yellow-500">
                      {result.title}
                    </h3>
                    <p className="text-gray-600">{result.description}</p>
                  </div>
                ))}
              </div>
            ) : searchQuery ? (
              <div className="text-center text-black animate-fade-in">
                No results found for "<span className="text-yellow-500">{searchQuery}</span>"
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <Footer />

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slide-up {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        @keyframes fade-in-up {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        
        .animate-slide-up {
          animation: slide-up 0.6s ease-out;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default SearchPage;